import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IdentityService } from '../identity/identity.service';

const UNLOCKED_EXPIRED_SITES_KEY = 'unlockedExpiredSites';

/**
 * Lets a superadmin open an expired account that customers are blocked from.
 * Unlocks are per site id and live in sessionStorage so a page refresh keeps the
 * admin in, while closing the tab locks the account again.
 */
@Injectable()
export class ExpiredAccountAccessService {
  private readonly identityService = inject(IdentityService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly unlockedSiteIds = signal<readonly number[]>(readUnlockedSiteIds());

  // For UI that has to react to the unlock. Signing out clears the set, so losing the admin role
  // always moves the source signal too and this cannot serve a stale value.
  public readonly unlockedSiteIdsForAdmin = computed<readonly number[]>(() =>
    this.identityService.currentIdentity?.isAdmin() ? this.unlockedSiteIds() : [],
  );

  constructor() {
    this.identityService.onSignout.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.clear());
  }

  // Always re-checks the role, so a forged sessionStorage value gains a non-admin nothing.
  public isUnlocked(siteId: number | undefined): boolean {
    if (siteId === undefined || !this.identityService.currentIdentity?.isAdmin()) {
      return false;
    }
    return this.unlockedSiteIds().includes(siteId);
  }

  public unlock(siteId: number): void {
    if (this.unlockedSiteIds().includes(siteId)) {
      return;
    }
    this.persist([...this.unlockedSiteIds(), siteId]);
  }

  public lock(siteId: number): void {
    this.persist(this.unlockedSiteIds().filter((id) => id !== siteId));
  }

  public clear(): void {
    this.persist([]);
  }

  private persist(siteIds: readonly number[]): void {
    this.unlockedSiteIds.set(siteIds);
    try {
      sessionStorage.setItem(UNLOCKED_EXPIRED_SITES_KEY, JSON.stringify(siteIds));
    } catch {
      // A full or unavailable sessionStorage only costs the unlock on refresh.
    }
  }
}

function readUnlockedSiteIds(): readonly number[] {
  try {
    const stored = sessionStorage.getItem(UNLOCKED_EXPIRED_SITES_KEY);
    if (!stored) {
      return [];
    }
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((id): id is number => typeof id === 'number');
  } catch {
    return [];
  }
}
